import client from 'core/axios';
import handleError from 'core/axios/handleError';
import { handleJSONResponse } from 'core/axios/handleResponse';
import RequestMethod from 'core/axios/RequestMethod';

import { isNonEmptyArray } from 'utils/arrays';

export const getCurrentSchoolYear = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl-schools/current-school-year/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCaseloadCategoryList = ({ version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl-schools/caseload-category/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getK12SchoolList = ({ version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/sl-schools/schools/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getK12SchoolWith12thGradersList = ({
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/sl-schools/schools/with-12th-graders/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getDistrictEmailTemplate = ({ slug, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/sl-schools/organization-email-templates/${slug}/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateDistrictEmailTemplate = ({
  slug,
  payload,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/sl-schools/organization-email-templates/${slug}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getK12Schools = ({ version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/sl-schools/schools/`,
      method: RequestMethod.GET,
      params: {
        ...params,
        pathname: window.location.pathname,
      },
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getK12SchoolsWithAlum = ({ version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/sl-schools/schools/with-alum/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getDistrictSettings = ({ version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/sl-schools/district-settings/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateDistrictSettings = ({ version = 'v1', payload = {} } = {}) =>
  client
    .request({
      url: `${version}/sl-schools/district-settings/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateSchool = ({ version = 'v1', id, payload = {} } = {}) =>
  client
    .request({
      url: `${version}/sl-schools/schools/${id}/`,
      method: RequestMethod.PATCH,
      data: payload,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getK12Districts = ({ version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/sl-schools/districts/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getK12AndNcesSchools = async ({
  version = 'v1',
  params = {},
} = {}) => {
  let data = [];
  const search = params.search || '';
  const [
    { status: k12SchoolStatus, data: k12SchoolData },
    { status: NcesSchoolStatus, data: NcesSchoolData },
  ] = await Promise.all([
    client.request({
      url: `${version}/sl-schools/schools/`,
      method: RequestMethod.GET,
      params: {
        search,
        highSchoolsOnly: true,
        allDistricts: true,
        removeDemoDistricts: true,
        ...params,
      },
    }),
    client.request({
      url: `${version}/common/autocomplete/`,
      method: RequestMethod.GET,
      params: {
        term: search,
        app_label: 'sl_schools',
        model_name: 'NCESSchool',
        offset: 0,
        limit: 10,
        fields: 'school_name',
      },
    }),
  ]);
  if (k12SchoolStatus === 200 && isNonEmptyArray(k12SchoolData?.results)) {
    data = [...data, ...k12SchoolData.results];
  }
  if (NcesSchoolStatus === 200 && isNonEmptyArray(NcesSchoolData?.results)) {
    data = [
      ...data,
      ...NcesSchoolData.results.map(t => ({
        ...t,
        type: 'nces',
      })),
    ];
  }

  return { data: { results: data } };
};

export const getDistrictConnections = ({ version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/sl-schools/districts/connections/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getDistrictConnectionsFilters = ({
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/sl-schools/districts/connections-context/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getSortedDistrictConnections = ({
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/sl-schools/districts/connections-sorted/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getK12SchoolTagGroupList = ({
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/sl-schools/school-tag-groups/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getK12SchoolTagList = ({ version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/sl-schools/school-tags/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getStudentsWithGuardiansCount = ({
  version = 'v1',
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/sl-schools/district-settings/students-with-guardians-count/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const SCHOOL_FILTERS = ['name', 'pk', 'k12_school_tags', 'school_type'];

export const getK12SchoolNotificationEmails = ({
  version = 'v1',
  schoolId,
  params = {},
} = {}) =>
  client
    .request({
      url: `${version}/sl-schools/schools/${schoolId}/notification-emails/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const registerModuleAccessUserInterest = ({
  version = 'v1',
  data,
} = {}) =>
  client
    .request({
      url: `${version}/sl-schools/module_access_user_interest/`,
      method: RequestMethod.POST,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getDistrictEventStats = ({ version = 'v1', params } = {}) =>
  client
    .request({
      url: `${version}/sl-schools/district-event-statistics/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getDistrictEventStatsById = ({
  version = 'v1',
  districtHashId,
  params,
} = {}) =>
  client
    .request({
      url: `${version}/sl-schools/district-event-statistics/${districtHashId}/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getSchoolStatsByDistrict = ({
  version = 'v1',
  districtHashId,
  params,
} = {}) =>
  client
    .request({
      url: `/api/${version}/sl-schools/district-event-statistics/${districtHashId}/school-statistics/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);
